import { useState, useEffect } from 'react';
import { CheckCircle2, Circle, Sparkles, Loader2 } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useAuth } from '../../../hooks/useAuth';
import { setupScoreService, type SetupScore } from '../../../services/setupScoreService';
import { Logger } from '../../../utils/logger';

const CHECK_KEYS: Record<string, string> = {
  positioning: 'setupCheckPositioning',
  sources: 'setupCheckSources',
  channels: 'setupCheckChannels',
  topics: 'setupCheckTopics',
  multiple_sources: 'setupCheckMultipleSources',
  connected_channel: 'setupCheckConnectedChannel',
};

export function SetupScoreCard() {
  const { t } = useLanguage();
  const { currentSpace } = useAuth();
  const [data, setData] = useState<SetupScore | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  useEffect(() => {
    if (!currentSpace?.id) return;
    setIsLoading(true);
    setupScoreService
      .getScore(currentSpace.id)
      .then(setData)
      .catch((err) => Logger.error('Failed to load setup score', err))
      .finally(() => setIsLoading(false));
  }, [currentSpace?.id]);

  const handleAnalyze = async () => {
    if (!currentSpace?.id) return;
    setIsAnalyzing(true);
    try {
      const result = await setupScoreService.analyzeSetup(currentSpace.id);
      setData(result);
    } catch (err) {
      Logger.error('Failed to analyze setup', err);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const s = t.settings as Record<string, string>;

  if (isLoading) {
    return (
      <div className="p-4 flex items-center justify-center" style={{ color: 'var(--color-text-secondary)' }}>
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const scoreColor =
    data.score >= 80 ? 'var(--color-success)' :
    data.score >= 50 ? 'var(--color-warning)' :
    // V4-FRONT-COLOR-DESTRUCTIVE-UNDEFINED 2026-07-30 — `--color-destructive` n'est
    // émise par personne (ColorContext expose `--color-danger`). Sans fallback, la
    // couleur devenait invalide et le score tombait en couleur héritée.
    'var(--color-danger)';

  return (
    <div className="space-y-3">
      {/* Score bar */}
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
          {s.setupScoreLabel}
        </span>
        <span className="text-sm font-bold" style={{ color: scoreColor }}>
          {data.score}/100
        </span>
      </div>
      <div className="w-full h-2 rounded-full" style={{ backgroundColor: 'var(--color-border)' }}>
        <div
          className="h-2 rounded-full transition-all duration-500"
          style={{ width: `${data.score}%`, backgroundColor: scoreColor }}
        />
      </div>

      {/* Checklist */}
      <ul className="space-y-1.5 mt-3">
        {Object.entries(data.checks).map(([key, passed]) => (
          <li key={key} className="flex items-center gap-2 text-xs">
            {passed ? (
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-success)' }} />
            ) : (
              <Circle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-text-secondary)' }} />
            )}
            <span style={{ color: passed ? 'var(--color-text-primary)' : 'var(--color-text-secondary)' }}>
              {s[CHECK_KEYS[key]] || key}
            </span>
          </li>
        ))}
      </ul>

      {/* AI Analysis */}
      {data.aiAnalysis && (
        <div
          className="mt-3 p-3 rounded-lg text-xs leading-relaxed"
          style={{
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {data.aiAnalysis}
        </div>
      )}

      {/* Analyze button */}
      <button
        onClick={handleAnalyze}
        disabled={isAnalyzing}
        className="w-full flex items-center justify-center gap-2 px-3 py-2 mt-2 rounded-lg text-xs font-medium transition-colors cursor-pointer"
        style={{
          backgroundColor: 'var(--color-primary)',
          color: 'var(--color-text-light)',
          opacity: isAnalyzing ? 0.7 : 1,
        }}
      >
        {isAnalyzing ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Sparkles className="w-4 h-4" />
        )}
        {s.setupAnalyze}
      </button>
    </div>
  );
}
